import React, { useState, useEffect } from "react";
import {
  X,
  ShieldCheck,
  Star,
  Leaf,
  Award,
  Sparkles,
  Eye,
  Heart,
  Share2,
  ZoomIn,
  CheckCircle,
  Truck,
  Zap,
  Globe,
  Users,
  TrendingUp,
  ArrowRight,
  RefreshCw,
  Info,
  AlertCircle,
  ShoppingCart
} from "lucide-react";

const EcoQualityFeatures = () => {
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const qualityFeatures = [
    {
      id: 1,
      title: "100% Biodegradable",
      image: "images/qualité2.jpg",
      icon: Leaf,
      description: "Our products are made from completely biodegradable materials that naturally decompose without harming the environment.",
      benefits: [
        "Reduces plastic waste",
        "Safe for soil and water",
        "Compostable in 90 days",
        "No toxic residues"
      ],
      color: "from-green-500 to-emerald-600",
      stats: "90% faster decomposition than plastic"
    },
    {
      id: 2,
      title: "Premium Quality",
      image: "images/qualité.jpg",
      icon: Award,
      description: "We use only the highest quality materials and manufacturing processes to ensure durability and longevity.",
      benefits: [
        "Superior durability",
        "Reinforced stitching",
        "Quality tested",
        "Long-lasting design"
      ],
      color: "from-blue-500 to-indigo-600",
      stats: "5x stronger than standard bags"
    },
    {
      id: 3,
      title: "Water Resistant",
      image: "images/qualité3.jpg",
      icon: ShieldCheck,
      description: "Advanced water-resistant technology keeps your items dry while maintaining eco-friendly properties.",
      benefits: [
        "Protects contents",
        "Weather resistant",
        "Easy to clean",
        "Maintains shape"
      ],
      color: "from-purple-500 to-pink-600",
      stats: "99% water resistance"
    },
    {
      id: 4,
      title: "Carbon Neutral",
      image: "images/qualité4.jpg",
      icon: Globe,
      description: "Our entire production process is carbon neutral, contributing to a healthier planet for future generations.",
      benefits: [
        "Zero carbon footprint",
        "Renewable energy",
        "Sustainable sourcing",
        "Climate positive"
      ],
      color: "from-orange-500 to-red-600",
      stats: "100% carbon neutral production"
    },
  ];

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 right-20 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200 rounded-full px-6 py-3 mb-6">
              <Sparkles className="w-5 h-5 text-green-600" />
              <span className="text-sm font-semibold text-green-700">Quality Features</span>
            </div>

            <h2 className="text-5xl font-bold bg-gradient-to-r from-gray-900 via-green-800 to-emerald-800 bg-clip-text text-transparent mb-6">
              Eco Quality Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover the exceptional quality features that make our eco-friendly products
              superior in sustainability, durability, and environmental impact.
            </p>
          </div>

          {/* Quality Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 gap-y-6 px-2 sm:px-4">

            {qualityFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={feature.id}
                  className={`group relative bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 overflow-hidden transition-all duration-500 transform hover:scale-105 hover:shadow-2xl cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  style={{
                    animationDelay: `${index * 150}ms`
                  }}
                  onClick={() => handleFeatureClick(feature)}
                >
                  {/* Image Container */}
                  <div className="relative h-48 sm:h-64 overflow-hidden">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />

                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Feature Icon */}
                    <div className={`absolute top-4 right-4 bg-gradient-to-r ${feature.color} text-white px-4 py-3 rounded-2xl shadow-lg`}>
                      <IconComponent className="w-6 h-6" />
                    </div>

                    {/* Stats Badge */}
                    <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-lg text-gray-800 px-3 py-2 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0">
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold">{feature.stats}</span>
                      </div>
                    </div>

                    {/* View Button */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0">
                      <button className="bg-white/90 backdrop-blur-lg text-gray-800 px-4 py-2 rounded-2xl font-semibold text-sm flex items-center space-x-2 shadow-lg">
                        <Eye className="w-4 h-4" />
                        <span>Learn More</span>
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 sm:p-6">

                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-green-700 transition-colors duration-300">
                        {feature.title}
                      </h3>
                      <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {feature.description}
                    </p>

                    {/* Benefits */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {feature.benefits.slice(0, 2).map((benefit, idx) => (
                        <span
                          key={idx}
                          className="bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 text-xs font-medium px-3 py-1 rounded-full border border-green-200"
                        >
                          {benefit}
                        </span>
                      ))}
                    </div>

                    {/* Stats and CTA */}
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        <span className="font-semibold text-green-600">{feature.stats}</span>
                      </div>

                      <div className="flex items-center space-x-2 text-green-600 group-hover:text-green-700 transition-colors duration-300">
                        <span className="text-sm font-semibold">Learn More</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Features Section */}
          <div className="mt-20">
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-12">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Our Products</h3>
                <p className="text-gray-600 text-lg">Quality, sustainability, and innovation in every product</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 gap-y-6 px-2 sm:px-4">

                <div className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Leaf className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Eco-Friendly</h4>
                  <p className="text-gray-600">100% biodegradable and sustainable materials</p>
                </div>

                <div className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Premium Quality</h4>
                  <p className="text-gray-600">Highest quality standards and materials</p>
                </div>

                <div className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-3xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Truck className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Fast Delivery</h4>
                  <p className="text-gray-600">Quick and reliable shipping worldwide</p>
                </div>

                <div className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-600 rounded-3xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Innovation</h4>
                  <p className="text-gray-600">Cutting-edge sustainable technology</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quality Feature Modal */}
      {selectedFeature && (
        <div className="fixed inset-0 z-[9999] min-h-screen flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
        <div className="relative bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 w-full max-w-[95vw] max-h-[90vh] overflow-y-auto">

            {/* Close Button */}
            <button
              className="absolute top-6 right-6 z-10 w-10 h-10 bg-white/80 backdrop-blur-lg rounded-2xl flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-white/90 transition-all duration-300 transform hover:scale-110"
              onClick={() => setSelectedFeature(null)}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col lg:flex-row">

              {/* Image Section */}
              <div className="relative w-full h-64 sm:h-80 lg:h-auto lg:w-1/2">
                <img
                  src={selectedFeature.image}
                  alt={selectedFeature.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>

                {/* Feature Icon */}
                <div className={`absolute top-4 left-4 bg-gradient-to-r ${selectedFeature.color} text-white px-4 py-3 rounded-2xl shadow-lg`}>
                  <selectedFeature.icon className="w-6 h-6" />
                </div>

                {/* Stats Badge */}
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-lg text-gray-800 px-4 py-3 rounded-2xl shadow-lg">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <span className="font-semibold">{selectedFeature.stats}</span>
                  </div>
                </div>
              </div>


              {/* Content Section */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="mb-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`w-16 h-16 bg-gradient-to-r ${selectedFeature.color} rounded-3xl flex items-center justify-center`}>
                      <selectedFeature.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-3xl lg:text-4xl font-bold text-gray-900">
                      {selectedFeature.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {selectedFeature.description}
                  </p>
                </div>

                {/* Benefits */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Benefits</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedFeature.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
                    <div className="flex items-center space-x-3 mb-2">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                      <h4 className="text-lg font-semibold text-gray-900">Performance Stats</h4>
                    </div>
                    <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                      {selectedFeature.stats}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <button className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                    <div className="flex items-center justify-center space-x-3">
                      <Info className="w-6 h-6" />
                      <span>Learn More</span>
                    </div>
                  </button>

                  <button className="px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105">
                    <Share2 className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }

  @media (max-width: 640px) {
    .group:hover {
      transform: none !important;
      box-shadow: none !important;
    }
  }
`}</style>

    </div>
  );
};

export default EcoQualityFeatures;